import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../../institutionAdmin.endpoints";
import type { LibraryBook, LibraryCopy, LibraryIssue, LibraryOverdueItem, LibrarySettings } from "../types";

const LIBRARY = INSTITUTION_ADMIN_ENDPOINTS.FACILITIES.LIBRARY;

export interface FetchBooksParams {
  search?: string;
  category?: string;
  page?: number;
  limit?: number;
}

export interface FetchBooksResult {
  books: LibraryBook[];
  categories: string[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export async function fetchBooks(institutionId: number, params: FetchBooksParams = {}): Promise<FetchBooksResult> {
  const { data } = await privateClient.get(LIBRARY.BOOKS(institutionId), { params });
  return {
    books: data.books ?? [],
    categories: data.categories ?? [],
    pagination: data.pagination ?? { page: 1, limit: 20, total: 0, totalPages: 1 },
  };
}

export async function createBook(
  institutionId: number,
  payload: { title: string; author?: string; isbn?: string; category?: string; shelfLocation?: string; coverUrl?: string; totalCopies?: number }
): Promise<LibraryBook> {
  const { data } = await privateClient.post(LIBRARY.BOOKS(institutionId), payload);
  return data.book;
}

export async function updateBook(
  institutionId: number,
  bookId: number,
  payload: { title?: string; author?: string; isbn?: string; category?: string; shelfLocation?: string; coverUrl?: string; totalCopies?: number }
): Promise<LibraryBook> {
  const { data } = await privateClient.patch(LIBRARY.BOOK(institutionId, bookId), payload);
  return data.book;
}

export async function deleteBook(institutionId: number, bookId: number): Promise<void> {
  await privateClient.delete(LIBRARY.BOOK(institutionId, bookId));
}

export async function uploadBookCover(file: File): Promise<string> {
  const form = new FormData();
  form.append("cover", file);
  const { data } = await privateClient.post(INSTITUTION_ADMIN_ENDPOINTS.UPLOADS.BOOK_COVER, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.coverUrl;
}

export async function fetchCopies(institutionId: number, bookId: number): Promise<LibraryCopy[]> {
  const { data } = await privateClient.get(LIBRARY.COPIES(institutionId, bookId));
  return data.copies ?? [];
}

export async function updateCopyStatus(
  institutionId: number,
  bookId: number,
  copyId: number,
  status: "AVAILABLE" | "LOST" | "DAMAGED"
): Promise<LibraryBook> {
  const { data } = await privateClient.patch(LIBRARY.COPY(institutionId, bookId, copyId), { status });
  return data.book;
}

export async function issueBook(
  institutionId: number,
  payload: { bookId: number; studentId?: number; staffId?: number; dueDate: string; academicYearId?: number }
): Promise<LibraryIssue> {
  const { data } = await privateClient.post(LIBRARY.ISSUE(institutionId), payload);
  return data.issue;
}

export async function returnBook(institutionId: number, issueId: number): Promise<LibraryIssue> {
  const { data } = await privateClient.post(LIBRARY.RETURN(institutionId, issueId));
  return data.issue;
}

export async function renewIssue(institutionId: number, issueId: number, newDueDate: string): Promise<LibraryIssue> {
  const { data } = await privateClient.post(LIBRARY.RENEW(institutionId, issueId), { newDueDate });
  return data.issue;
}

export async function fetchStudentIssues(
  institutionId: number,
  studentId: number,
  academicYearId?: number
): Promise<LibraryIssue[]> {
  const { data } = await privateClient.get(LIBRARY.STUDENT_ISSUES(institutionId, studentId), { params: { academicYearId } });
  return data.issues ?? [];
}

export async function fetchStaffIssues(institutionId: number, staffId: number): Promise<LibraryIssue[]> {
  const { data } = await privateClient.get(LIBRARY.STAFF_ISSUES(institutionId, staffId));
  return data.issues ?? [];
}

export async function fetchOverdue(institutionId: number): Promise<LibraryOverdueItem[]> {
  const { data } = await privateClient.get(LIBRARY.OVERDUE(institutionId));
  return data.overdue ?? [];
}

export async function fetchSettings(institutionId: number): Promise<LibrarySettings> {
  const { data } = await privateClient.get(LIBRARY.SETTINGS(institutionId));
  return data.settings;
}

export async function updateSettings(institutionId: number, finePerDay: number): Promise<LibrarySettings> {
  const { data } = await privateClient.put(LIBRARY.SETTINGS(institutionId), { finePerDay });
  return data.settings;
}

export interface ScannedStudentLibrary {
  student: { id: number; name: string; studentCode: string | null; photoUrl: string | null; className: string | null };
  summary: { borrowed: number; overdue: number; totalIssued: number; fines: number };
  issues: LibraryIssue[];
  membership?: { required: boolean; enrolled: boolean };
}

export async function scanStudent(institutionId: number, token: string): Promise<ScannedStudentLibrary> {
  const { data } = await privateClient.get(LIBRARY.SCAN(institutionId, token));
  return data;
}

export async function fetchLibraryPeople(
  institutionId: number
): Promise<{ students: { id: number; name: string }[]; staff: { id: number; name: string }[] }> {
  const { data } = await privateClient.get(LIBRARY.PEOPLE(institutionId));
  return { students: data.students ?? [], staff: data.staff ?? [] };
}

export async function setMembershipRequired(institutionId: number, membershipRequired: boolean): Promise<LibrarySettings> {
  const { data } = await privateClient.put(LIBRARY.SETTINGS(institutionId), { membershipRequired });
  return data.settings;
}

export interface LibraryMember {
  studentId: number;
  name: string;
  studentCode: string | null;
  photoUrl: string | null;
  className: string | null;
  addedAt: string;
}

export async function fetchMembers(institutionId: number): Promise<LibraryMember[]> {
  const { data } = await privateClient.get(LIBRARY.MEMBERS(institutionId));
  return data.members ?? [];
}

export async function addMembers(institutionId: number, studentIds: number[]): Promise<void> {
  await privateClient.post(LIBRARY.MEMBERS(institutionId), { studentIds });
}

export async function removeMember(institutionId: number, studentId: number): Promise<void> {
  await privateClient.delete(LIBRARY.MEMBER(institutionId, studentId));
}
